/**
 * Shared Module Configuration for Multi-Tenant Organizations
 * Defines API endpoints, status maps, validation helpers, and portal URL formatters.
 */

export const MODULE_CONFIG = {
  name: "Tenants",
  title: "Tenants",
  subtitle: "Multi-tenant organizations, branded subdomains, portal configurations, and tenant lifecycle management.",
  singular: "Tenant",
  plural: "Tenants",
  routeBase: "/tenants",

  // Primary Platform Multi-Tenancy Base Domain
  portalBaseDomain: "charlessmithassociates.com",
  portalBaseUrl: "https://charlessmithassociates.com",

  // API Endpoints
  endpoints: {
    listing: "tenants",
    detail: (id) => `tenants/${id}`,
    add: "tenants",
    update: (id) => `tenants/${id}`,
    statusChange: (id) => `tenants/${id}/status`,
    delete: (id) => `tenants/${id}`,
    uploadLogo: "upload/logo",
    tenantLogo: (id) => `tenants/${id}/logo`,
    checkSubdomain: "tenants/check-subdomain",
  },

  // Lifecycle Status Filter Options
  statusOptions: [
    { id: "ALL", name: "All Statuses" },
    { id: "ACTIVE", name: "Active" },
    { id: "TRIAL", name: "Trial" },
    { id: "PAST_DUE", name: "Past Due" },
    { id: "SUSPENDED", name: "Suspended" },
    { id: "CANCELLED", name: "Cancelled" },
  ],

  // High-contrast, accessibility-first status badges (Zero light grey)
  statusMap: {
    ACTIVE: {
      label: "Active",
      bgClass: "bg-emerald-100 text-emerald-950 border-emerald-300 font-bold",
      dotClass: "bg-emerald-600",
    },
    active: {
      label: "Active",
      bgClass: "bg-emerald-100 text-emerald-950 border-emerald-300 font-bold",
      dotClass: "bg-emerald-600",
    },
    TRIAL: {
      label: "Trial",
      bgClass: "bg-sky-100 text-sky-950 border-sky-300 font-bold",
      dotClass: "bg-sky-600",
    },
    trial: {
      label: "Trial",
      bgClass: "bg-sky-100 text-sky-950 border-sky-300 font-bold",
      dotClass: "bg-sky-600",
    },
    PAST_DUE: {
      label: "Past Due",
      bgClass: "bg-amber-100 text-amber-950 border-amber-300 font-bold",
      dotClass: "bg-amber-600",
    },
    past_due: {
      label: "Past Due",
      bgClass: "bg-amber-100 text-amber-950 border-amber-300 font-bold",
      dotClass: "bg-amber-600",
    },
    SUSPENDED: {
      label: "Suspended",
      bgClass: "bg-rose-100 text-rose-950 border-rose-300 font-bold",
      dotClass: "bg-rose-600",
    },
    suspended: {
      label: "Suspended",
      bgClass: "bg-rose-100 text-rose-950 border-rose-300 font-bold",
      dotClass: "bg-rose-600",
    },
    CANCELLED: {
      label: "Cancelled",
      bgClass: "bg-slate-200 text-slate-900 border-slate-300 font-bold",
      dotClass: "bg-slate-600",
    },
    cancelled: {
      label: "Cancelled",
      bgClass: "bg-slate-200 text-slate-900 border-slate-300 font-bold",
      dotClass: "bg-slate-600",
    },
  },
};

/**
 * Build tenant customer portal URL from subdomain
 */
export const getPortalUrl = (subdomain) => {
  if (!subdomain) return MODULE_CONFIG.portalBaseUrl;
  const cleanSub = subdomain.toLowerCase().trim().replace(/[^a-z0-9-]/g, "");
  return `https://${cleanSub}.${MODULE_CONFIG.portalBaseDomain}`;
};

/**
 * Format tenant logo URL:
 * - If already full URL like "https://iknowapi.jcsoftwaresolution.in/logos/...", show as it is
 * - If full URL like "http://iknowapi.jcsoftwaresolution.in/logos/...", show as it is (upgrade to https if current page is https)
 * - If relative like "logos/logo-1792402351092-22378.jpeg", add "https://iknowapi.jcsoftwaresolution.in/"
 */
export const getTenantLogoUrl = (rawLogo) => {
  if (!rawLogo || typeof rawLogo !== "string") return "";
  const trimmed = rawLogo.trim();
  if (!trimmed) return "";

  // Already a full URL
  if (trimmed.startsWith("https://")) {
    return trimmed;
  }
  if (trimmed.startsWith("http://")) {
    if (typeof window !== "undefined" && window.location.protocol === "https:") {
      return trimmed.replace(/^http:\/\//i, "https://");
    }
    return trimmed;
  }

  // Relative path (e.g. "logos/logo-1792402351092-22378.jpeg")
  const cleanPath = trimmed.startsWith("/") ? trimmed.slice(1) : trimmed;
  return `https://iknowapi.jcsoftwaresolution.in/${cleanPath}`;
};

/**
 * Format full address string from address object
 */
export const formatAddress = (addr) => {
  if (!addr) return "—";
  if (typeof addr === "string") return addr;
  const parts = [addr.street, addr.city, addr.state, addr.zipCode, addr.country].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "—";
};

import {
  capitalize,
  capitalizeSentence,
  formatDate,
  formatTime,
  getInitials,
} from "../../methods/methods";

export {
  capitalize,
  capitalizeSentence,
  formatDate,
  formatTime,
  getInitials,
};
